import { memo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Button, Card, Flex, Select, Tag } from 'antd';

import { InfoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import axios from '../../../axios';

import { sortDates, sortNumbers, sortStrings } from '../../../helpers/filter';
import { hasPermission } from '../../../helpers/user';
import { validatePhone } from '../../../helpers/validate-phone';

import { DATE_FORMAT, EDIT_COMPONENT_TYPE } from '../../../constants/common';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { PLANS_STATUS, STATUS_COLOR } from '../../../constants/subscription';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';

import { useSetState } from '../../../hooks/useSetState';

import { updateUsersProfile } from '../../../redux/actions/admin';
import { updateUserSubscriptionStatus } from '../../../redux/actions/subscription';
import {
  archiveUsers,
  deleteUsers,
  getAllUsers,
  getUsers,
  sendSetPassword,
  setSelectedFilters
} from '../../../redux/actions/user';
import { confirmRoles } from '../../../redux/actions/userSlice';

import AppTable from '../../../components/AppTable';
import { COLUMN_SIZE } from '../../../components/AppTable/constants';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import { errorToastHandler, successToastHandler } from '../../../components/action_notifier';

import countries from '../../../countries.json';
import SubmitSubscriptionModal from '../../Modals/SubmitSubscriptionModal';

const commonColumnProperties = {
  ellipsis: true,
  searchable: true,
  sortDirections: ['ascend', 'descend']
};

function UserTable() {
  const dispatch = useDispatch();

  const { loading, permissions, selectedFilters, users } = useSelector(state => state.user);
  const { plans } = useSelector(state => state.subscription);
  const { roles } = useSelector(state => state.admin);

  const [filteredInfo, setFilteredInfo] = useState({});
  const [selection, setSelection] = useState({
    selectAll: false,
    selectedUsers: []
  });
  const [state, setState] = useSetState({
    archive_modal: false,
    delete_modal: false,
    archivedUsers: [],
    deletedUsers: []
  });
  const [isOpenSubmitSubscriptionModal, setOpenSubmitSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState({});

  const flattenedUsers = users?.map(user => ({
    ...user,
    city: user.address?.city || '',
    country: user?.address?.country || '',
    house_number: user.address?.house_number || '',
    postal_code: user.address?.postal_code || '',
    street: user.address?.street || '',
    plan: user.user_plans?.at(0)?.plan?.id,
    end_date: user.user_plans?.at(0)?.end_date,
    roles: user.roles?.map(r => r.id)
  }));
  const rolesOptions = [{ label: 'None', value: '' }].concat(
    roles?.map(role => ({ label: role.display_name, value: role.id })).sort((a, b) => sortStrings(a.label, b.label))
  );
  const countriesOptions = countries.map(country => ({ label: country, value: country }));

  const selectRecords = useCallback(
    selected => {
      setSelection(prevState => ({
        ...prevState,
        selectedUsers: selected,
        selectAll: users?.length === selected.length
      }));
    },
    [selection]
  );

  const handleTableChange = useCallback(
    (pagination, filters) => {
      setFilteredInfo(filters);
      dispatch(setSelectedFilters(filters));
    },
    [setFilteredInfo, dispatch]
  );

  const clearFilters = useCallback(() => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  }, [setFilteredInfo, dispatch]);

  const actions = initActions();

  const getTableData = data => {
    const copiedUsers = data.map(u => ({ ...u }));

    // setState({ tableData: [...copiedUsers.slice(0, 25)] });
  };

  function setArchivedUsers() {
    setState({ archivedUsers: selection.selectedUsers });
    toggleModal('archive_modal');
  }

  function setDeletedUsers() {
    setState({ deletedUsers: selection.selectedUsers });
    toggleModal('delete_modal');
  }

  function onClickSendSetPassword() {
    const emails = [];

    selection.selectedUsers.forEach(id => {
      const user = users.find(user => user.id === id);
      emails.push({ email: user.email });
    });

    dispatch(sendSetPassword(emails));
  }

  function onClickUpdateUserStatus(status) {
    const payload = { status, userIds: selection.selectedUsers };

    dispatch(updateUserSubscriptionStatus(payload)).then(() => dispatch(getAllUsers()));
  }

  async function confirmSelectedRoles() {
    try {
      await dispatch(confirmRoles(selection.selectedUsers)).unwrap();
      await dispatch(getAllUsers());
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  function initActions() {
    const isAnyUserSelected = selection.selectedUsers?.length;
    const user = selection.selectedUsers?.some(id => !users.find(u => u.id === id));

    return [
      {
        label: (
          <Link className="app-link" to="/new-user" disabled={!hasPermission([PERMISSIONS.USERS.CREATE], permissions)}>
            Add new user
          </Link>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={
              !isAnyUserSelected || !!user?.deleted_at || !hasPermission([PERMISSIONS.USERS.DELETE], permissions)
            }
            onClick={setArchivedUsers}>
            Archive user
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyUserSelected || !hasPermission([PERMISSIONS.USERS.DELETE], permissions)}
            onClick={setDeletedUsers}>
            Delete user
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyUserSelected || !hasPermission([PERMISSIONS.USERS.UPDATE_ALL], permissions)}
            onClick={onClickSendSetPassword}>
            Send &#34;set password&#34;
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyUserSelected || !hasPermission([PERMISSIONS.USERS.UPDATE_ALL], permissions)}
            onClick={() => onClickUpdateUserStatus(PLANS_STATUS.ACTIVE)}>
            Activate subscription
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyUserSelected || !hasPermission([PERMISSIONS.USERS.UPDATE_ALL], permissions)}
            onClick={() => onClickUpdateUserStatus(PLANS_STATUS.INACTIVE)}>
            Deactivate subscription
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyUserSelected || !hasPermission([PERMISSIONS.USERS.UPDATE_ALL], permissions)}
            onClick={confirmSelectedRoles}>
            Confirm roles
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!Object.values(selectedFilters)?.length}
            onClick={clearFilters}>
            Clear all filters
          </Button>
        )
      }
    ];
  }

  const toggleModal = modal => {
    setState(state => ({ ...state, [modal]: !state[modal] }));
  };

  const handleInputChange = ([updatedUser]) => {
    const address = ['house_number', 'street', 'city', 'country', 'postal_code'].reduce((result, key) => {
      if (updatedUser?.[key]) {
        result[key] = updatedUser?.[key];
      }

      return result;
    }, {});

    const updatedData = {
      ...updatedUser,
      address: { ...updatedUser?.address, ...address },
      roles: updatedUser?.roles.value ? [updatedUser?.roles.value] : [],
      work_phone:
        updatedUser?.work_phone ? (updatedUser?.work_phone.startsWith('+')
          ? updatedUser?.work_phone
          : `+${updatedUser?.work_phone}`)
          : updatedUser?.work_phone,
      private_phone:
        updatedUser?.private_phone ? (updatedUser?.private_phone.startsWith('+')
          ? updatedUser?.private_phone
          : `+${updatedUser?.private_phone}`)
          : updatedUser?.private_phone,
      user_plans:
        updatedUser?.end_date && Number.isInteger(updatedUser?.end_date?.value)
          ? [{ ...updatedUser.user_plans?.at(0), period: updatedUser?.end_date?.value }]
          : [...updatedUser.user_plans]
    };

    updateUsers(updatedData);
  };

  const handlePlanChange = (record, changedData) => {
    const updatedData = {
      ...record,
      user_plans: [...changedData]
    };

    updateUsers(updatedData);
  };

  const updateUsers = updatedData => {
    dispatch(updateUsersProfile({ users: [updatedData] })).then(data => {
      if (data) {
        setSelection({ selectAll: false, selectedUsers: [] });
        dispatch(getAllUsers());
      }

      successToastHandler(messages.UPDATED);
    });
  };

  const updateUserList = useCallback(query => {
    const payload = { query };
    dispatch(getUsers(payload));
  }, []);

  const onArchiveUsers = async () => {
    const payload = { ids: state.archivedUsers };

    try {
      await dispatch(archiveUsers(payload));
      setSelection({ selectAll: false, selectedUsers: [] });
      dispatch(getAllUsers());
      toggleModal('archive_modal');
    } catch (error) {
      errorToastHandler(messages.DEFAULT_ERROR);
    }
  };

  const onDeleteUsers = async () => {
    const payload = { ids: state.deletedUsers };

    try {
      await dispatch(deleteUsers(payload));
      setSelection({ selectAll: false, selectedUsers: [] });
      dispatch(getAllUsers());
      toggleModal('delete_modal');
    } catch (error) {
      errorToastHandler(messages.DEFAULT_ERROR);
    }
  };

  const columns = [
    {
      ...commonColumnProperties,
      title: 'User ID',
      dataIndex: 'id',
      configColumnKey: TABLE_CONFIG_COLUMNS.ID,
      filteredValue: filteredInfo.id || null,
      fixed: 'left',
      width: COLUMN_SIZE.XS,
      sorter: (a, b) => sortNumbers(a.id, b.id)
    },
    {
      ...commonColumnProperties,
      title: 'Email',
      dataIndex: 'email',
      configColumnKey: TABLE_CONFIG_COLUMNS.EMAIL,
      filteredValue: filteredInfo.email || null,
      width: COLUMN_SIZE.XXL,
      render: (value, record) => <a href={`mailto:${value}`}>{value}</a>,
      sorter: (a, b) => sortStrings(a.email, b.email)
    },
    {
      ...commonColumnProperties,
      title: 'Email Verification Date',
      dataIndex: 'email_verified_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.EMAIL_VERIFIED_AT,
      filteredValue: filteredInfo.email_verified_at || null,
      width: COLUMN_SIZE.XL,
      render: (value, record) => (value ? dayjs(value).format(DATE_FORMAT.DATETIME) : null),
      sorter: (a, b) => sortDates(a.email_verified_at, b.email_verified_at)
    },
    {
      ...commonColumnProperties,
      title: 'First Name',
      dataIndex: 'first_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.NAME,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.first_name || null,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.first_name, b.first_name)
    },
    {
      ...commonColumnProperties,
      title: 'Last Name',
      dataIndex: 'last_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.LAST_NAME,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.last_name || null,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.last_name, b.last_name)
    },
    {
      ...commonColumnProperties,
      title: 'Plan',
      dataIndex: 'plan',
      configColumnKey: TABLE_CONFIG_COLUMNS.PLAN,
      filteredValue: filteredInfo.plan || null,
      width: COLUMN_SIZE.XL,
      render: (value, record) => {
        const [planData] = record?.user_plans ?? [];
        const plan = planData?.plan;
        const planInfo = { value: plan?.id, label: plan?.title, ...plan };
        const plansFormatted = [
          ...(plans || []).map(pl => {
            return { value: pl.id, label: pl.title, ...pl };
          })
        ];

        return (
          <Flex justify="space-between" align="center" gap="small">
            <Select
              className="w-full"
              size="small"
              variant="borderless"
              style={{ height: '21px' }}
              value={planInfo}
              onChange={planId => {
                const payload = plansFormatted.find(p => p.id === planId);
                delete payload.value;
                delete payload.feature_options;
                delete payload.total_price;
                delete payload.label;
                handlePlanChange(record, [{ ...planData, plan: { ...payload } }]);
              }}
              options={plansFormatted}
            />
            <Button
              variant="filled"
              color="primary"
              disabled={!planData}
              icon={<InfoOutlined />}
              size="small"
              style={{ height: '21px' }}
              onClick={async () => {
                if (!planData) return;

                const resp = await axios.get(`subscription/user/${planData.id}`);
                setOpenSubmitSubscriptionModal(true);
                setSelectedPlan({
                  ...resp?.data?.plan,
                  period: resp?.data?.period
                });
              }}
            />
          </Flex>
        );
      }
    },
    {
      ...commonColumnProperties,
      align: 'center',
      title: 'Plan Status',
      dataIndex: 'planStatus',
      configColumnKey: TABLE_CONFIG_COLUMNS.STATUS,
      filteredValue: filteredInfo.planStatus || null,
      width: COLUMN_SIZE.SM,
      render: (value, record) => {
        const status = record?.user_plans?.at(0)?.status;
        return status ? (
          <Tag className="text-capitalize" color={STATUS_COLOR[status]}>
            {status}
          </Tag>
        ) : (
          <></>
        );
      }
    },
    {
      ...commonColumnProperties,
      title: 'Plan End Date',
      dataIndex: 'end_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.END_DATE,
      editConfig: {
        disabledToEdit: record => !record.plan,
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: [
          { value: 1, label: 'in 1 month' },
          { value: 3, label: 'in 3 months' },
          { value: 6, label: 'in 6 months' },
          { value: 12, label: 'in 12 months' }
        ]
      },
      filteredValue: filteredInfo.end_date || null,
      width: COLUMN_SIZE.XL,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATETIME) : null)
    },
    {
      ...commonColumnProperties,
      title: 'Work Phone',
      dataIndex: 'work_phone',
      configColumnKey: TABLE_CONFIG_COLUMNS.WORK_PHONE,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.work_phone || null,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.work_phone, b.work_phone),
      rules: [validatePhone('Work Phone')]
    },
    {
      ...commonColumnProperties,
      title: 'Mobile Phone',
      dataIndex: 'private_phone',
      configColumnKey: TABLE_CONFIG_COLUMNS.PRIVATE_PHONE,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.private_phone || null,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.private_phone, b.private_phone),
      rules: [validatePhone('Mobile Phone')]
    },
    {
      ...commonColumnProperties,
      title: 'Backup Email',
      dataIndex: 'backup_email',
      configColumnKey: TABLE_CONFIG_COLUMNS.BACKUP_EMAIL,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.backup_email || null,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.backup_email, b.backup_email),
      rules: [{ type: 'email', message: messages.FIELD_INVALID('Backup Email') }]
    },
    {
      ...commonColumnProperties,
      title: 'SMS enabled',
      dataIndex: 'is_sms_notify_enabled',
      configColumnKey: TABLE_CONFIG_COLUMNS.SMS_ENABLED,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ]
      },
      filteredValue: filteredInfo.is_sms_notify_enabled || null,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      width: COLUMN_SIZE.XS,
      onFilter: (value, record) => (value === 1 ? record?.is_sms_notify_enabled : !record?.is_sms_notify_enabled),
      render: (value, record) => (record.is_sms_notify_enabled ? 'Yes' : 'No'),
      sorter: (a, b) => sortStrings(a.is_sms_notify_enabled, b.is_sms_notify_enabled)
    },
    {
      ...commonColumnProperties,
      title: 'Email Notification Enabled',
      dataIndex: 'is_email_notify_enabled',
      configColumnKey: TABLE_CONFIG_COLUMNS.EMAIL_ENABLED,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false }
        ]
      },
      filteredValue: filteredInfo.is_email_notify_enabled || null,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      width: COLUMN_SIZE.XS,
      onFilter: (value, record) => (value === 1 ? record?.is_email_notify_enabled : !record?.is_email_notify_enabled),
      render: (value, record) => (record.is_email_notify_enabled ? 'Yes' : 'No'),
      sorter: (a, b) => sortStrings(a.is_email_notify_enabled, b.is_email_notify_enabled)
    },
    {
      ...commonColumnProperties,
      title: 'Street Name',
      dataIndex: 'street',
      configColumnKey: TABLE_CONFIG_COLUMNS.STREET,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.street || null,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.street, b.street)
    },
    {
      ...commonColumnProperties,
      title: 'House Number / Name / Farm Name',
      dataIndex: 'house_number',
      configColumnKey: TABLE_CONFIG_COLUMNS.HOUSE_NUMBER,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.house_number || null,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.house_number, b.house_number)
    },
    {
      ...commonColumnProperties,
      title: 'City / Town',
      dataIndex: 'city',
      configColumnKey: TABLE_CONFIG_COLUMNS.CITY,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.city || null,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.city, b.city)
    },
    {
      ...commonColumnProperties,
      title: 'Postal Code',
      dataIndex: 'postal_code',
      configColumnKey: TABLE_CONFIG_COLUMNS.POSTAL_CODE,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      filteredValue: filteredInfo.postal_code || null,
      width: COLUMN_SIZE.SM,
      sorter: (a, b) => sortNumbers(+a.postal_code, +b.postal_code)
    },
    {
      ...commonColumnProperties,
      title: 'Country',
      dataIndex: 'country',
      configColumnKey: TABLE_CONFIG_COLUMNS.COUNTRY,
      editConfig: { type: EDIT_COMPONENT_TYPE.SELECT, options: countriesOptions },
      filteredValue: filteredInfo.country || null,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.country, b.country)
    },
    {
      ...commonColumnProperties,
      title: 'Creation Date',
      dataIndex: 'created_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.CREATED_AT,
      filteredValue: filteredInfo.created_at || null,
      width: COLUMN_SIZE.XL,
      render: (value, record) => dayjs(value).format(DATE_FORMAT.DATETIME),
      sorter: (a, b) => sortDates(a.created_at, b.created_at)
    },
    {
      ...commonColumnProperties,
      title: 'Role',
      dataIndex: 'roles',
      configColumnKey: TABLE_CONFIG_COLUMNS.ROLES,
      editConfig: { type: EDIT_COMPONENT_TYPE.SELECT, options: rolesOptions || [] },
      filteredValue: filteredInfo.roles || null,
      render: (value, record) => record.userRoles?.at(0)?.newRole?.id ?? value[0] ?? 'None',
      noPadding: true,
      width: COLUMN_SIZE.XL
    },
    {
      ...commonColumnProperties,
      align: 'center',
      title: 'Role Status',
      dataIndex: 'roleStatus',
      configColumnKey: TABLE_CONFIG_COLUMNS.USER_ROLE_STATUS,
      filteredValue: filteredInfo.roleStatus || null,
      width: COLUMN_SIZE.SM,
      render: (value, record) => {
        const status = record?.userRoles?.at(0)?.status;
        return (
          status && (
            <Tag className="text-capitalize" color={STATUS_COLOR[status]}>
              {status}
            </Tag>
          )
        );
      }
    },
    {
      ...commonColumnProperties,
      title: 'SMS Count',
      dataIndex: 'sms_count',
      configColumnKey: TABLE_CONFIG_COLUMNS.SMS_COUNT,
      filteredValue: filteredInfo.sms_count || null,
      width: COLUMN_SIZE.LG,
      render: value => value ?? 0
    },
    {
      ...commonColumnProperties,
      title: 'Delete Date',
      dataIndex: 'deleted_at',
      configColumnKey: TABLE_CONFIG_COLUMNS.DELETED_AT,
      filteredValue: filteredInfo.deleted_at || null,
      width: COLUMN_SIZE.XL,
      render: (value, record) => (record.deleted_at ? dayjs(record.deleted_at).format(DATE_FORMAT.DATETIME) : null),
      sorter: (a, b) => sortDates(a.deleted_at, b.deleted_at)
    },
    {
      ...commonColumnProperties,
      title: 'Is Linked',
      dataIndex: 'is_linked',
      configColumnKey: TABLE_CONFIG_COLUMNS.IS_LINKED,
      filteredValue: filteredInfo.is_linked || null,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      width: COLUMN_SIZE.MD,
      render: (_, record) => (record.is_linked ? 'Yes' : 'No'),
      onFilter: (value, record) => value === record.is_linked
    },
    {
      ...commonColumnProperties,
      title: 'Linked account',
      dataIndex: 'linked_account',
      configColumnKey: TABLE_CONFIG_COLUMNS.LINKED_ACCOUNT,
      filteredValue: filteredInfo.linked_account || null,
      width: COLUMN_SIZE.MD,
      render: (_, record) => record.linked_account || 'None',
      onFilter: (value, record) => +value === record.linked_account
    }
  ];

  return (
    <>
      <Card>
        <AppTable
          description="List of Users"
          headerClass="py-2"
          searchPlaceholder="Search User"
          title="Users"
          actions={actions}
          baseColumns={columns}
          dataSource={flattenedUsers}
          loading={loading}
          tableConfigKey={TABLE_CONFIG_KEYS.ADMIN_USERS}
          handleOnChange={handleTableChange}
          handleOnSelectRecords={selectRecords}
          handleSearchChange={updateUserList}
          updateTableData={handleInputChange}
        />
      </Card>

      <SubmitSubscriptionModal
        title="User Plan"
        isEditDisabled={true}
        cancelText="Close"
        isOpen={isOpenSubmitSubscriptionModal}
        onCancel={() => {
          setSelectedPlan({});
          setOpenSubmitSubscriptionModal(false);
        }}
        plan={selectedPlan}
      />

      <ConfirmationModal
        isOpen={state.archive_modal}
        title="Archive users"
        description="Archiving this user account will set it to inactive and will prevent
          the user from accessing the application. Do you want to archive this
          user account?"
        submitText="Archive"
        onCancel={() => toggleModal('archive_modal')}
        onSubmit={onArchiveUsers}
      />

      <ConfirmationModal
        isOpen={state.delete_modal}
        title="Delete users"
        description="Deleting user account(s) will permanently delete all data
          for these accounts.  Are you sure you want to continue?"
        submitText="Delete"
        onCancel={() => toggleModal('delete_modal')}
        onSubmit={onDeleteUsers}
      />
    </>
  );
}

export default memo(UserTable);
